import { useTranslation } from 'react-i18next'
import { CreditCard, Loader2 } from 'lucide-react'
import { useCreateCheckout, type PaymentType } from '../hooks/use-create-checkout'
import { cn } from '@/shared/lib/utils'
import pricingConfig from '@/config/pricing.json'

// Get one-shot plan price from pricing config (launch price: 29€)
const oneShotPlan = pricingConfig.plans.find(p => p.id === 'one-shot')
const SCOPING_PRICE = oneShotPlan?.price_display ?? 29
const SCOPING_CURRENCY = oneShotPlan?.currency ?? 'EUR'

interface PaymentButtonProps {
  teamId: string
  projectId: string
  paymentType: PaymentType
  quoteId?: string // Required for development payments - the accepted quote ID
  displayAmount?: number // Amount to display (in cents) - for development payments
  displayCurrency?: string // Currency for display - for development payments
  className?: string
  variant?: 'primary' | 'outline'
  size?: 'default' | 'lg'
}

export function PaymentButton({
  teamId,
  projectId,
  paymentType,
  quoteId,
  displayAmount,
  displayCurrency,
  className,
  variant = 'primary',
  size = 'default',
}: PaymentButtonProps) {
  const { t, i18n } = useTranslation()
  const createCheckout = useCreateCheckout()

  const handleClick = () => {
    createCheckout.mutate({
      teamId,
      projectId,
      paymentType,
      quoteId,
    })
  }

  const getPrice = (): string => {
    if (paymentType === 'scoping') {
      const formattedPrice = new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency: SCOPING_CURRENCY,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(SCOPING_PRICE)
      return i18n.language === 'fr' ? `${formattedPrice} HT` : formattedPrice
    }
    if (displayAmount) {
      return new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency: displayCurrency || (i18n.language === 'fr' ? 'EUR' : 'USD')
      }).format(displayAmount / 100)
    }
    return ''
  }

  const priceDisplay = getPrice()
  const isLoading = createCheckout.isPending

  const label = paymentType === 'scoping'
    ? t('payment.payForScoping')
    : t('payment.payForDevelopment')

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        variant === 'primary' && 'bg-accent text-white hover:bg-accent/90',
        variant === 'outline' && 'border border-accent text-accent hover:bg-accent/10',
        size === 'default' && 'px-4 py-2 text-sm',
        size === 'lg' && 'px-6 py-3 text-base',
        className
      )}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <CreditCard className="h-4 w-4" />
      )}
      <span>{label}</span>
      {priceDisplay && (
        <span className={cn(
          'ml-1',
          variant === 'primary' ? 'text-white/80' : 'text-accent/80'
        )}>
          ({priceDisplay})
        </span>
      )}
    </button>
  )
}
